import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  BookOpen,
  CheckCircle2,
  ChevronRight,
  ClipboardCheck,
  FileText,
  Flame,
  GraduationCap,
  Sparkles,
  Star,
  Target,
  TrendingUp,
  Zap,
  Swords,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  lmsGetMeLearner,
  lmsGetEnrollments,
  lmsGetProgress,
  lmsGetLearnerXp,
  lmsGetMyPaymentRecords,
  lmsDownloadPaymentReceipt,
  lmsGetMyHomework,
  getCourseWithCurriculum,
  type LmsEnrollmentSummary,
  type LmsProgress,
  type LmsPaymentRecord,
  type LmsHomeworkAssignment,
  type CourseLesson,
} from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

/** XP thresholds for levels (level 1 = 0–99, 2 = 100–249, …). */
const LEVELS = [
  { level: 1, minXp: 0, title: 'Sprout' },
  { level: 2, minXp: 100, title: 'Explorer' },
  { level: 3, minXp: 250, title: 'Rising' },
  { level: 4, minXp: 500, title: 'Achiever' },
  { level: 5, minXp: 1000, title: 'Star' },
  { level: 6, minXp: 2000, title: 'Champion' },
  { level: 7, minXp: 4000, title: 'Master' },
  { level: 8, minXp: 8000, title: 'Legend' },
];

function getLevelInfo(totalXp: number): { level: number; title: string; progressToNext: number } {
  let idx = LEVELS.length - 1;
  for (let i = 0; i < LEVELS.length; i++) {
    if (totalXp < LEVELS[i].minXp) {
      idx = i - 1;
      break;
    }
  }
  const current = LEVELS[Math.max(0, idx)];
  const next = LEVELS[idx + 1];
  const prevThreshold = current.minXp;
  const nextThreshold = next ? next.minXp : prevThreshold + 1000;
  const progressToNext = nextThreshold > prevThreshold
    ? Math.min(100, (100 * (totalXp - prevThreshold)) / (nextThreshold - prevThreshold))
    : 100;
  return {
    level: current.level,
    title: current.title,
    progressToNext,
  };
}

type EnrollmentWithProgress = LmsEnrollmentSummary & {
  progress: LmsProgress | null;
};

/** Next lesson to continue (from one course). */
type NextLesson = {
  lesson: CourseLesson;
  courseTitle: string;
  programSlug: string;
  courseSlug: string;
  sectionId: string;
  sectionTitle: string;
};

const WEEK_DAYS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

export function StudentDashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [enrollments, setEnrollments] = useState<EnrollmentWithProgress[]>([]);
  const [totalXp, setTotalXp] = useState(0);
  const [nextLessons, setNextLessons] = useState<NextLesson[]>([]);
  const [paymentRecords, setPaymentRecords] = useState<LmsPaymentRecord[]>([]);
  const [homework, setHomework] = useState<LmsHomeworkAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingReceiptId, setDownloadingReceiptId] = useState<string | null>(null);
  const [quizTab, setQuizTab] = useState<'quizzes' | 'homework'>('quizzes');

  const loadDashboard = useCallback(async () => {
    if (!user || user.role === 'admin') return;
    setLoading(true);
    setError(null);
    try {
      const { learnerId: lid } = await lmsGetMeLearner();
      if (!lid) {
        setEnrollments([]);
        setTotalXp(0);
        setNextLessons([]);
        setPaymentRecords([]);
        setHomework([]);
        setLoading(false);
        return;
      }
      const [enrollmentsRes, xpRes, paymentRes, hwRes] = await Promise.all([
        lmsGetEnrollments(lid),
        lmsGetLearnerXp(lid),
        lmsGetMyPaymentRecords().catch(() => ({ records: [] })),
        lmsGetMyHomework().catch(() => ({ assignments: [] })),
      ]);
      const list = enrollmentsRes.enrollments ?? [];
      setTotalXp(xpRes?.totalXp ?? 0);

      const withProgress: EnrollmentWithProgress[] = await Promise.all(
        list.map(async (e) => {
          try {
            const progress = await lmsGetProgress(e.id);
            return { ...e, progress };
          } catch {
            return { ...e, progress: null };
          }
        })
      );
      setEnrollments(withProgress);
      setPaymentRecords(paymentRes?.records ?? []);
      setHomework(hwRes.assignments ?? []);

      // Next lessons: pick first enrollment that has incomplete lessons, fetch its curriculum, find next
      const firstIncomplete = withProgress.find((e) => e.progress && e.progress.completedCount < e.progress.totalLessons);
      if (firstIncomplete) {
        try {
          const course = await getCourseWithCurriculum(
            firstIncomplete.course.programSlug,
            firstIncomplete.course.slug
          );
          const completedSet = new Set(firstIncomplete.progress!.completedLessonIds);
          const next: NextLesson[] = [];
          for (const section of course.sections ?? []) {
            for (const lesson of section.lessons ?? []) {
              if (!completedSet.has(lesson.id)) {
                next.push({
                  lesson,
                  courseTitle: course.title,
                  programSlug: course.program_slug,
                  courseSlug: course.slug,
                  sectionId: section.id,
                  sectionTitle: section.title,
                });
                if (next.length >= 4) break;
              }
            }
            if (next.length >= 4) break;
          }
          setNextLessons(next);
        } catch {
          setNextLessons([]);
        }
      } else {
        setNextLessons([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard');
      setEnrollments([]);
      setTotalXp(0);
      setNextLessons([]);
      setPaymentRecords([]);
      setHomework([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  if (user?.role === 'admin') {
    navigate('/admin', { replace: true });
    return null;
  }
  if (user?.role === 'tutor') {
    navigate('/tutor/dashboard', { replace: true });
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
          <div className="h-8 w-48 bg-muted rounded animate-pulse mb-6" />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="rounded-xl border border-border bg-card p-4 h-20 animate-pulse" />
            ))}
          </div>
          <div className="rounded-xl border border-border bg-card p-6 h-64 animate-pulse" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="text-center">
          <p className="text-destructive mb-4">{error}</p>
          <Button onClick={() => loadDashboard()} variant="outline">Try again</Button>
        </div>
      </div>
    );
  }

  const levelInfo = getLevelInfo(totalXp);
  const totalLessonsAll = enrollments.reduce((s, e) => s + (e.progress?.totalLessons ?? 0), 0);
  const completedLessonsAll = enrollments.reduce((s, e) => s + (e.progress?.completedCount ?? 0), 0);
  const overallPercent = totalLessonsAll > 0 ? Math.round((completedLessonsAll / totalLessonsAll) * 100) : 0;
  const streakDays = 0; // TODO: backend streak when available

  return (
    <div className="min-h-screen bg-background">
      {/* Hero header */}
      <section className="relative overflow-hidden bg-card border-b border-border">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-20 -left-20 w-72 h-72 rounded-full bg-accent/8 blur-[100px]" />
          <div className="absolute -bottom-10 right-0 w-52 h-52 rounded-full bg-accent/5 blur-[80px]" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 sm:py-6 relative">
          <div className="flex items-center gap-4 mb-5">
            <div className="relative shrink-0">
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-accent/15 border-2 border-accent/30 flex items-center justify-center">
                <Zap className="w-7 h-7 sm:w-8 sm:h-8 text-accent" />
              </div>
              <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-accent text-accent-foreground flex items-center justify-center text-[10px] font-bold border-2 border-card">
                {levelInfo.level}
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-lg sm:text-xl font-display font-bold text-foreground">My Dashboard</h1>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-accent/15 text-accent border border-accent/30">
                  {levelInfo.title}
                </span>
              </div>
              <div className="flex items-center gap-2 mt-1.5">
                <Zap className="w-3.5 h-3.5 text-accent shrink-0" />
                <span className="text-xs text-muted-foreground font-semibold whitespace-nowrap">
                  {totalXp.toLocaleString()} XP
                </span>
                <div className="flex-1 max-w-[200px] h-1.5 rounded-full bg-secondary overflow-hidden">
                  <div
                    className="h-full rounded-full bg-accent transition-all duration-700 ease-out"
                    style={{ width: `${levelInfo.progressToNext}%` }}
                  />
                </div>
                <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                  → Lvl {levelInfo.level + 1}
                </span>
              </div>
            </div>
            <div className="hidden md:flex items-center gap-2 px-4 py-2.5 rounded-xl bg-secondary/80 border border-border">
              <Flame className="w-5 h-5 text-accent" />
              <div>
                <p className="text-lg font-display font-bold text-foreground leading-none">{streakDays}</p>
                <p className="text-[9px] text-muted-foreground">day streak</p>
              </div>
              <div className="flex gap-0.5 ml-2">
                {WEEK_DAYS.map((d, i) => (
                  <div
                    key={i}
                    className={`w-5 h-5 rounded-md flex items-center justify-center text-[8px] font-bold ${
                      i < streakDays ? 'bg-accent/20 text-accent' : 'bg-secondary text-muted-foreground/40'
                    }`}
                  >
                    {d}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="flex md:hidden items-center gap-2 px-3 py-2 rounded-xl bg-secondary/60 border border-border w-fit">
            <Flame className="w-4 h-4 text-accent" />
            <span className="text-sm font-display font-bold text-foreground">{streakDays}</span>
            <span className="text-[10px] text-muted-foreground">day streak</span>
          </div>
        </div>
      </section>

      <div className="max-w-5xl 2xl:max-w-6xl mx-auto px-4 sm:px-6 py-5 sm:py-6 relative">
        <div className="xl:pr-64 2xl:pr-72">
          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            {[
              { label: 'Courses', value: enrollments.length, Icon: BookOpen },
              { label: 'Lessons done', value: `${completedLessonsAll}/${totalLessonsAll}`, Icon: CheckCircle2 },
              { label: 'Overall', value: `${overallPercent}%`, Icon: TrendingUp },
              { label: 'Total XP', value: totalXp.toLocaleString(), Icon: Zap },
            ].map((s) => (
              <div
                key={s.label}
                className="rounded-xl border border-border bg-card p-3 sm:p-4 flex items-center gap-3"
              >
                <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center shrink-0">
                  <s.Icon className="w-5 h-5 text-accent" />
                </div>
                <div className="min-w-0">
                  <p className="text-base sm:text-lg font-display font-bold text-foreground leading-tight truncate">
                    {s.value}
                  </p>
                  <p className="text-[10px] sm:text-xs text-muted-foreground">{s.label}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Level journey */}
          <div className="mb-6 rounded-xl border border-border bg-card p-4">
            <div className="flex items-center gap-2 mb-3">
              <Target className="w-4 h-4 text-accent" />
              <span className="text-sm font-display font-semibold text-foreground">Level journey</span>
            </div>
            <div className="flex items-center overflow-x-auto gap-1 pb-1">
              {LEVELS.map((lvl, i) => {
                const isCurrent = lvl.level === levelInfo.level;
                const isUnlocked = totalXp >= lvl.minXp;
                return (
                  <div key={lvl.level} className="flex items-center shrink-0">
                    <div
                      className={`flex flex-col items-center transition-all ${
                        isCurrent ? 'scale-110 z-10' : ''
                      }`}
                    >
                      <div
                        className={`w-10 h-10 rounded-xl flex items-center justify-center border-2 transition-all ${
                          isCurrent
                            ? 'bg-accent text-accent-foreground border-accent shadow-lg'
                            : isUnlocked
                              ? 'bg-accent/15 text-accent border-accent/30'
                              : 'bg-secondary text-muted-foreground/40 border-border'
                        }`}
                      >
                        <Zap className="w-5 h-5" />
                      </div>
                      <span
                        className={`text-[9px] mt-1 font-medium whitespace-nowrap ${
                          isCurrent
                            ? 'text-accent font-bold'
                            : isUnlocked
                              ? 'text-foreground'
                              : 'text-muted-foreground/40'
                        }`}
                      >
                        {lvl.title}
                      </span>
                    </div>
                    {i < LEVELS.length - 1 && (
                      <div
                        className={`w-6 h-0.5 rounded-full mx-0.5 ${
                          isUnlocked && totalXp >= (LEVELS[i + 1]?.minXp ?? 0) ? 'bg-accent' : 'bg-border'
                        }`}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
            {/* Left column */}
            <div className="lg:col-span-3 space-y-5">
              {/* Continue learning */}
              <div className="rounded-xl border border-border bg-card overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-accent" />
                    <span className="text-sm font-display font-semibold text-foreground">Continue learning</span>
                  </div>
                  <Link
                    to="/courses"
                    className="text-xs text-accent hover:underline flex items-center gap-0.5"
                  >
                    All courses <ChevronRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
                <div className="p-3">
                  {nextLessons.length === 0 ? (
                    <div className="text-center py-8">
                      <GraduationCap className="w-10 h-10 text-muted-foreground/20 mx-auto mb-3" />
                      <p className="text-sm text-muted-foreground mb-3">
                        Enroll in a course to start learning
                      </p>
                      <Button
                        asChild
                        size="sm"
                        className="bg-accent text-accent-foreground hover:bg-accent/90"
                      >
                        <Link to="/courses">Explore courses</Link>
                      </Button>
                    </div>
                  ) : (
                    <ul className="space-y-1">
                      {nextLessons.map(
                        ({ lesson, courseTitle, programSlug, courseSlug, sectionId, sectionTitle }) => (
                          <li key={lesson.id}>
                            <Link
                              to={`/courses/${programSlug}/courses/${courseSlug}/sections/${sectionId}`}
                              className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-secondary/60 transition-all group"
                            >
                              <div className="w-9 h-9 rounded-lg bg-secondary flex items-center justify-center text-muted-foreground group-hover:text-accent group-hover:bg-accent/10 transition-all shrink-0">
                                <BookOpen className="w-4 h-4" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-foreground truncate group-hover:text-accent transition-colors">
                                  {lesson.title}
                                </p>
                                <p className="text-xs text-muted-foreground truncate">
                                  {courseTitle} · {sectionTitle}
                                </p>
                              </div>
                              <span className="text-xs text-accent font-semibold shrink-0">
                                +{lesson.xpPerLesson} XP
                              </span>
                            </Link>
                          </li>
                        )
                      )}
                    </ul>
                  )}
                </div>
              </div>

              {/* My courses */}
              {enrollments.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <BookOpen className="w-4 h-4 text-accent" />
                    <span className="text-sm font-display font-semibold text-foreground">My courses</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {enrollments.map((e) => {
                      const pct = e.progress
                        ? e.progress.totalLessons > 0
                          ? Math.round((e.progress.completedCount / e.progress.totalLessons) * 100)
                          : 0
                        : 0;
                      return (
                        <Link
                          key={e.id}
                          to={`/courses/${e.course.programSlug}/courses/${e.course.slug}`}
                          className="block p-4 rounded-xl border border-border bg-card hover:border-accent/40 hover:shadow-md transition-all group hover:-translate-y-0.5"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="text-sm font-display font-semibold text-foreground truncate group-hover:text-accent transition-colors">
                              {e.course.title}
                            </h3>
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-secondary text-muted-foreground shrink-0">
                              {pct}%
                            </span>
                          </div>
                          <div className="h-1.5 rounded-full bg-secondary overflow-hidden mb-2">
                            <div
                              className="h-full rounded-full bg-accent transition-all"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground">
                            <span>
                              {e.progress?.completedCount ?? 0}/{e.progress?.totalLessons ?? 0} lessons
                            </span>
                            <span className="ml-auto flex items-center gap-0.5">
                              <Star className="w-3 h-3 text-accent" /> {e.course.programTitle}
                            </span>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Payment records / Receipts */}
              <div
                id="payments-section"
                className="rounded-xl border border-border bg-card overflow-hidden"
              >
                <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-accent" />
                    <span className="text-sm font-display font-semibold text-foreground">
                      Payment records
                    </span>
                  </div>
                </div>
                <div className="p-3">
                  {paymentRecords.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-6">
                      No payment records yet. When you pay for a course via M-Pesa, your receipt will
                      appear here.
                    </p>
                  ) : (
                    <ul className="space-y-2">
                      {paymentRecords.map((record) => (
                        <li
                          key={record.id}
                          className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-border bg-secondary/30"
                        >
                          <div className="w-9 h-9 rounded-lg bg-accent/10 flex items-center justify-center shrink-0">
                            <FileText className="w-4 h-4 text-accent" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">
                              {record.courseTitle}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              KES {record.amountKes.toLocaleString('en-KE')} ·{' '}
                              {new Date(record.paidAt).toLocaleDateString('en-KE', {
                                dateStyle: 'medium',
                              })}
                            </p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            className="shrink-0 text-xs"
                            disabled={downloadingReceiptId === record.id}
                            onClick={async () => {
                              setDownloadingReceiptId(record.id);
                              try {
                                const blob = await lmsDownloadPaymentReceipt(record.id);
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = `HomelandHub-receipt-${record.id.slice(0, 8)}.pdf`;
                                a.click();
                                URL.revokeObjectURL(url);
                              } catch {
                                // ignore
                              } finally {
                                setDownloadingReceiptId(null);
                              }
                            }}
                          >
                            {downloadingReceiptId === record.id ? '…' : 'Download receipt'}
                          </Button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>

            {/* Right column */}
            <div className="lg:col-span-2 space-y-5">
              {/* Assessments */}
              <div className="rounded-xl border border-border bg-card overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                  <div className="flex items-center gap-2">
                    <Swords className="w-4 h-4 text-accent" />
                    <span className="text-sm font-display font-semibold text-foreground">
                      Assessments
                    </span>
                  </div>
                  <div className="flex p-0.5 rounded-lg bg-secondary/80">
                    <button
                      type="button"
                      onClick={() => setQuizTab('quizzes')}
                      className={`px-2.5 py-1 rounded-md text-[10px] font-medium transition-all ${
                        quizTab === 'quizzes'
                          ? 'bg-card text-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      Quizzes
                    </button>
                    <button
                      type="button"
                      onClick={() => setQuizTab('homework')}
                      className={`px-2.5 py-1 rounded-md text-[10px] font-medium transition-all ${
                        quizTab === 'homework'
                          ? 'bg-card text-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      Homework ({homework.length})
                    </button>
                  </div>
                </div>
                <div className="p-3">
                  {quizTab === 'quizzes' ? (
                    <div className="text-center py-6 text-xs text-muted-foreground">
                      Quizzes are part of your lessons. Complete quiz lessons and mark them complete to
                      earn XP.
                    </div>
                  ) : homework.length === 0 ? (
                    <div className="text-center py-6 text-xs text-muted-foreground">
                      No homework yet. Your tutor will assign work here.
                    </div>
                  ) : (
                    <ul className="space-y-1">
                      {homework.slice(0, 4).map((a) => {
                        const daysLeft =
                          a.dueDate != null
                            ? Math.ceil(
                                (new Date(a.dueDate).getTime() - Date.now()) /
                                  (1000 * 60 * 60 * 24)
                              )
                            : null;
                        return (
                          <li
                            key={a.id}
                            className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-secondary/60 transition-all"
                          >
                            <div className="w-9 h-9 rounded-lg bg-accent/10 flex items-center justify-center shrink-0">
                              <ClipboardCheck className="w-4 h-4 text-accent" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-foreground truncate">{a.title}</p>
                              <p className="text-[10px] text-muted-foreground truncate">
                                {a.courseTitle}
                                {a.dueDate ? ' · Due ' + a.dueDate : ''}
                              </p>
                            </div>
                            <div className="flex flex-col items-end gap-0.5 shrink-0">
                              <span className="text-[9px] capitalize text-muted-foreground">
                                {a.status}
                              </span>
                              {daysLeft != null && (
                                <span
                                  className={`text-[9px] ${
                                    daysLeft < 0
                                      ? 'text-destructive'
                                      : daysLeft <= 2
                                        ? 'text-africa-orange'
                                        : 'text-muted-foreground'
                                  }`}
                                >
                                  {daysLeft < 0
                                    ? `${Math.abs(daysLeft)}d overdue`
                                    : daysLeft === 0
                                      ? 'Due today'
                                      : `${daysLeft}d left`}
                                </span>
                              )}
                            </div>
                          </li>
                        );
                      })}
                      <li className="pt-1 text-center">
                        <Link
                          to="/homework"
                          className="text-xs text-accent hover:underline flex items-center justify-center gap-0.5"
                        >
                          View all homework <ChevronRight className="w-3.5 h-3.5" />
                        </Link>
                      </li>
                    </ul>
                  )}
                </div>
              </div>

              {/* Overall progress ring */}
              <div id="overall-progress" className="rounded-xl border border-border bg-card p-5">
                <div className="flex items-center gap-5">
                  <div className="relative w-24 h-24 shrink-0">
                    <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
                      <path
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                        fill="none"
                        stroke="hsl(var(--secondary))"
                        strokeWidth="3"
                      />
                      <path
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                        fill="none"
                        stroke="hsl(var(--accent))"
                        strokeWidth="3"
                        strokeDasharray={`${overallPercent}, 100`}
                        strokeLinecap="round"
                        className="transition-all duration-1000 ease-out"
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-xl font-display font-bold text-foreground">
                        {overallPercent}%
                      </span>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-display font-semibold text-foreground mb-1">
                      Overall progress
                    </p>
                    <p className="text-xs text-muted-foreground mb-3">
                      {completedLessonsAll} of {totalLessonsAll} lessons
                    </p>
                    <div className="grid grid-cols-2 gap-1.5">
                      <div className="text-center py-1.5 rounded-lg bg-secondary/50">
                        <p className="text-sm font-bold text-foreground">{enrollments.length}</p>
                        <p className="text-[9px] text-muted-foreground">Enrolled</p>
                      </div>
                      <div className="text-center py-1.5 rounded-lg bg-accent/10">
                        <p className="text-sm font-bold text-accent">
                          {
                            enrollments.filter(
                              (e) =>
                                e.progress &&
                                e.progress.totalLessons > 0 &&
                                e.progress.completedCount >= e.progress.totalLessons
                            ).length
                          }
                        </p>
                        <p className="text-[9px] text-muted-foreground">Completed</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {enrollments.length > 0 && (
                <Button
                  asChild
                  className="w-full bg-accent text-accent-foreground hover:bg-accent/90 rounded-xl h-11 gap-2"
                >
                  <Link
                    to={`/courses/${enrollments[0].course.programSlug}/courses/${enrollments[0].course.slug}`}
                  >
                    Continue learning <ChevronRight className="w-4 h-4" />
                  </Link>
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Quick access side panel (large screens) */}
        <aside className="hidden xl:block absolute right-0 top-5 w-60">
          <div className="rounded-2xl border border-border bg-card shadow-md p-4 space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
              Quick access
            </p>
            <button
              type="button"
              onClick={() => navigate('/homework')}
              className="w-full flex items-center justify-between px-3 py-2 rounded-lg border border-border hover:border-accent/40 hover:bg-secondary/40 text-left text-xs transition-all"
            >
              <span className="flex items-center gap-2">
                <ClipboardCheck className="w-4 h-4 text-accent" />
                <span className="font-medium text-foreground">Homework</span>
              </span>
              <ChevronRight className="w-3 h-3 text-muted-foreground" />
            </button>
            <button
              type="button"
              onClick={() => navigate('/courses')}
              className="w-full flex items-center justify-between px-3 py-2 rounded-lg border border-border hover:border-accent/40 hover:bg-secondary/40 text-left text-xs transition-all"
            >
              <span className="flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-accent" />
                <span className="font-medium text-foreground">All courses</span>
              </span>
              <ChevronRight className="w-3 h-3 text-muted-foreground" />
            </button>
            <a
              href="#payments-section"
              className="w-full flex items-center justify-between px-3 py-2 rounded-lg border border-border hover:border-accent/40 hover:bg-secondary/40 text-left text-xs transition-all"
            >
              <span className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-accent" />
                <span className="font-medium text-foreground">Payment records</span>
              </span>
              <ChevronRight className="w-3 h-3 text-muted-foreground" />
            </a>
            <a
              href="#overall-progress"
              className="w-full flex items-center justify-between px-3 py-2 rounded-lg border border-border hover:border-accent/40 hover:bg-secondary/40 text-left text-xs transition-all"
            >
              <span className="flex items-center gap-2">
                <Target className="w-4 h-4 text-accent" />
                <span className="font-medium text-foreground">Progress</span>
              </span>
              <ChevronRight className="w-3 h-3 text-muted-foreground" />
            </a>
          </div>
        </aside>
      </div>
    </div>
  );
}
